// components/SelfPaced/StudentCourseDetailPage.js
// Course preview + enroll / purchase — themed, responsive, dark-mode ready
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Spinner, Modal } from 'react-bootstrap';
import {
  ArrowLeft, Clock, BarChart3, BookOpen, Users, Globe, Play,
  CheckCircle2, Award, ShieldCheck, ChevronDown, ChevronUp,
  Lock, Presentation, File, PenLine, CirclePlay,
} from 'lucide-react';
import { AppButton, AppBadge, BackButton, EmptyState, ProgressBar, Tabs, TabsList, TabsTrigger, TabsContent } from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { getPublicCourse, purchaseCourse, verifyPurchase, getMyEnrollments, streamLessonVideo } from '../../utils/selfPacedApi';
import useRazorpay from '../../hooks/useRazorpay';
import DOMPurify from 'dompurify';
import './_self-paced-pages.scss';

const LESSON_ICONS = {
  video: <CirclePlay size={15} />,
  presentation: <Presentation size={15} />,
  quiz: <PenLine size={15} />,
  document: <File size={15} />,
};

/* ── Curriculum Accordion Item ────────────────────────────── */
const ModuleAccordion = ({ mod, index, openIndex, toggle, onPreviewClick }) => {
  const isOpen = openIndex === index;
  const lessonCount = mod.lessons?.length || 0;
  return (
    <div className={`spd-accordion ${isOpen ? 'spd-accordion--open' : ''}`}>
      <button className="spd-accordion__header" onClick={() => toggle(index)}>
        <span className="spd-accordion__title">
          <strong>{index + 1}.</strong> {mod.title || mod.name}
        </span>
        <span className="spd-accordion__meta">
          {lessonCount} lesson{lessonCount !== 1 ? 's' : ''}
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {isOpen && (
        <ul className="spd-accordion__body">
          {(mod.lessons || []).map((l, li) => (
            <li
              key={l._id || li}
              className={`spd-accordion__lesson${l.isPreview ? ' spd-accordion__lesson--preview' : ''}`}
              onClick={() => l.isPreview && l.type === 'video' && onPreviewClick?.(l)}
            >
              <span className="spd-accordion__lesson-icon">
                {LESSON_ICONS[l.type] || LESSON_ICONS.video}
              </span>
              <span className="spd-accordion__lesson-title">{l.title || l.name}</span>
              {l.isPreview && <AppBadge color="emerald" size="sm">Free Preview</AppBadge>}
              {!l.isPreview && <Lock size={13} className="spd-accordion__lock" />}
              {l.duration > 0 && <span className="spd-accordion__lesson-dur">{l.duration >= 60 ? `${Math.floor(l.duration / 60)}h ${Math.round(l.duration % 60)}m` : `${Math.round(l.duration)}m`}</span>}
            </li>
          ))}
          {lessonCount === 0 && (
            <li className="spd-accordion__lesson spd-accordion__lesson--empty">No lessons yet</li>
          )}
        </ul>
      )}
    </div>
  );
};

/* ── Main Page ──────────────────────────────────────────────── */
export default function StudentCourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.state?.from === 'enrollments' ? '/s/self-paced/enrollments' : '/s/self-paced';
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [openIndex, setOpenIndex] = useState(0);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [previewLesson, setPreviewLesson] = useState(null);
  const [previewStreamUrl, setPreviewStreamUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  /* Fetch course + enrollment status */
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [courseRes, enrollRes] = await Promise.allSettled([
        getPublicCourse(id),
        getMyEnrollments(),
      ]);
      const raw = courseRes.status === 'fulfilled' ? (courseRes.value.data?.data || courseRes.value.data) : null;
      const c = raw?.course ? { ...raw.course, curriculum: raw.curriculum } : raw;
      const enrollments = enrollRes.status === 'fulfilled' ? (enrollRes.value.data?.data || enrollRes.value.data || []) : [];
      setCourse(c);
      setEnrolled(enrollments.some(e => (e.course?._id || e.course) === id));
    } catch {
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  /* ── Purchase Flow ─────────────────────────────────────── */
  const handlePurchase = async () => {
    if (!course) return;
    const isFree = !course.price || course.price === 0;

    setPurchasing(true);
    try {
      const res = await purchaseCourse(course._id);
      const data = res.data?.data || res.data;

      if (isFree || data?.enrolled) {
        // Free course → auto-enrolled
        setToast({ show: true, message: 'Enrolled successfully!', type: 'success' });
        setEnrolled(true);
        setPurchasing(false);
        return;
      }

      // Paid → open Razorpay
      const checkoutData = {
        razorpayKeyId: data.razorpayKeyId,
        order: { id: data.orderId, amount: Math.round(data.amount * 100), currency: data.currency || 'INR' },
        description: `Self-paced course: ${course.title || course.name}`,
      };
      openCheckout(
        checkoutData,
        async (response) => {
          try {
            await verifyPurchase({
              courseId: course._id,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });
            setToast({ show: true, message: 'Payment successful! Enrolled.', type: 'success' });
            setEnrolled(true);
          } catch {
            setToast({ show: true, message: 'Payment verification failed.', type: 'error' });
          }
          setPurchasing(false);
        },
        (err) => {
          setToast({ show: true, message: err?.reason || 'Payment cancelled.', type: 'error' });
          setPurchasing(false);
        },
      );
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Enrollment failed.', type: 'error' });
      setPurchasing(false);
    }
  };

  /* ── Helpers ───────────────────────────────────────────── */
  const toggleAccordion = (idx) => setOpenIndex(prev => (prev === idx ? -1 : idx));

  const handlePreviewClick = async (lesson) => {
    setPreviewLesson(lesson);
    setPreviewStreamUrl(null);
    setPreviewLoading(true);
    try {
      const res = await streamLessonVideo(lesson._id);
      const url = res.data?.data?.url || res.data?.url;
      if (url) setPreviewStreamUrl(url);
      else throw new Error('No URL');
    } catch {
      setToast({ show: true, message: 'Unable to load preview. Please sign in first.', type: 'error' });
      setPreviewLesson(null);
    }
    setPreviewLoading(false);
  };

  const hasDiscount = course?.discountPrice && course?.discountPrice < course?.price;
  const isFree = !course?.price || course?.price === 0;
  const discountPct = hasDiscount ? Math.round((1 - course.discountPrice / course.price) * 100) : 0;
  const modules = course?.modules || course?.curriculum || [];
  const totalLessons = modules.reduce((n, m) => n + (m.lessons?.length || 0), 0);

  // Parse newline-separated strings into arrays
  const toList = (v) => (Array.isArray(v) ? v : typeof v === 'string' ? v.split('\n').map(s => s.trim()).filter(Boolean) : []);
  const isHtml = (s) => typeof s === 'string' && /<[a-z][\s\S]*>/i.test(s);
  const highlights = toList(course?.highlights);
  const prerequisites = toList(course?.prerequisites);
  const syllabus = toList(course?.syllabus);

  /* ── Render ────────────────────────────────────────────── */
  if (loading) return <div className="sp-page"><div className="sp-loading"><Spinner animation="border" variant="secondary" /></div></div>;
  if (!course) return <div className="sp-page"><EmptyState title="Course not found" description="This course may have been removed." /></div>;

  return (
    <div className="sp-page">
      {/* Banner */}
      <div className="spd-banner">
        <div className="spd-banner__main">
          <div style={{ marginBottom: '0.5rem' }}>
            <BackButton onClick={() => navigate(backPath)} />
          </div>
          {course.category && <AppBadge variant="secondary" color="primary">{course.category}</AppBadge>}
          <h1 className="spd-banner__title">{course.title || course.name}</h1>
          {course.description && <p className="spd-banner__desc">{course.description}</p>}

          <div className="spd-banner__meta">
            {course.level && <span><BarChart3 size={14} /> {course.level}</span>}
            {course.language && <span><Globe size={14} /> {course.language}</span>}
            {course.duration && <span><Clock size={14} /> {course.duration}</span>}
            {totalLessons > 0 && <span><BookOpen size={14} /> {totalLessons} lessons</span>}
            {course.enrollmentCount > 0 && <span><Users size={14} /> {course.enrollmentCount} students</span>}
          </div>
        </div>

        {/* Sidebar Card */}
        <div className="spd-sidebar">
          <div className="spd-sidebar__card">
            {(course.thumbnailUrl || course.thumbnail) && (
              <div className="spd-sidebar__thumb">
                <img src={course.thumbnailUrl || course.thumbnail} alt="" />
                <div className="spd-sidebar__play"><Play size={28} /></div>
              </div>
            )}

            <div className="spd-sidebar__body">
              <div className="spd-sidebar__pricing">
                {enrolled ? (
                  <AppBadge color="primary" size="sm">✓ Owned</AppBadge>
                ) : isFree ? (
                  <span className="spd-sidebar__price spd-sidebar__price--free">Free</span>
                ) : hasDiscount ? (
                  <>
                    <span className="spd-sidebar__price">₹{course.discountPrice?.toLocaleString('en-IN')}</span>
                    <span className="spd-sidebar__price-original">₹{course.price?.toLocaleString('en-IN')}</span>
                    <AppBadge color="emerald" size="sm">{discountPct}% off</AppBadge>
                  </>
                ) : (
                  <span className="spd-sidebar__price">₹{course.price?.toLocaleString('en-IN')}</span>
                )}
              </div>

              {enrolled ? (
                <AppButton block onClick={() => navigate(`/s/self-paced/${id}/learn`)}>
                  <Play size={15} /> Continue Learning
                </AppButton>
              ) : (
                <AppButton block onClick={handlePurchase} disabled={purchasing || rzpLoading}>
                  {purchasing ? <><Spinner size="sm" animation="border" /> Processing…</> : isFree ? 'Enroll for Free' : 'Buy Now'}
                </AppButton>
              )}

              <ul className="spd-sidebar__perks">
                <li><CheckCircle2 size={14} /> Full lifetime access</li>
                <li><ShieldCheck size={14} /> Certificate of completion</li>
                <li><BookOpen size={14} /> {totalLessons} lessons</li>
                {modules.length > 0 && <li><Award size={14} /> {modules.length} modules</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Content */}
      {(() => {
        const hasCurriculum = modules.length > 0;
        const hasSyllabus = syllabus.length > 0 || isHtml(course?.syllabus);
        const hasPrereqs = prerequisites.length > 0 || isHtml(course?.prerequisites);
        const hasHighlights = highlights.length > 0 || isHtml(course?.highlights);
        if (!hasCurriculum && !hasSyllabus && !hasPrereqs && !hasHighlights) return null;
        const defaultTab = hasCurriculum ? 'curriculum' : hasSyllabus ? 'syllabus' : hasPrereqs ? 'prerequisites' : 'highlights';
        return (
          <section className="spd-section">
            <Tabs defaultValue={defaultTab}>
              <TabsList>
                {hasCurriculum && <TabsTrigger value="curriculum">Course Content</TabsTrigger>}
                {hasSyllabus && <TabsTrigger value="syllabus">Syllabus</TabsTrigger>}
                {hasPrereqs && <TabsTrigger value="prerequisites">Prerequisites</TabsTrigger>}
                {hasHighlights && <TabsTrigger value="highlights">What you'll learn</TabsTrigger>}
              </TabsList>

              {hasCurriculum && (
                <TabsContent value="curriculum">
                  <div className="spd-curriculum">
                    {modules.map((mod, i) => (
                      <ModuleAccordion key={mod._id || i} mod={mod} index={i} openIndex={openIndex} toggle={toggleAccordion} onPreviewClick={handlePreviewClick} />
                    ))}
                  </div>
                </TabsContent>
              )}

              {hasSyllabus && (
                <TabsContent value="syllabus">
                  {isHtml(course?.syllabus) ? (
                    <div className="spd-prerequisites spd-prerequisites--html ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.syllabus) }} />
                  ) : (
                    <ul className="spd-prerequisites">
                      {syllabus.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  )}
                </TabsContent>
              )}

              {hasPrereqs && (
                <TabsContent value="prerequisites">
                  {isHtml(course?.prerequisites) ? (
                    <div className="spd-prerequisites spd-prerequisites--html ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.prerequisites) }} />
                  ) : (
                    <ul className="spd-prerequisites">
                      {prerequisites.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  )}
                </TabsContent>
              )}

              {hasHighlights && (
                <TabsContent value="highlights">
                  {isHtml(course?.highlights) ? (
                    <div className="spd-prerequisites spd-prerequisites--html ql-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.highlights) }} />
                  ) : (
                    <ul className="spd-prerequisites">
                      {highlights.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  )}
                </TabsContent>
              )}
            </Tabs>
          </section>
        );
      })()}

      <CommonToaster show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast(t => ({ ...t, show: false }))} />

      {/* ── Preview Lesson Video Modal ──────────────────────── */}
      <Modal
        show={!!previewLesson}
        onHide={() => { setPreviewLesson(null); setPreviewStreamUrl(null); }}
        centered
        size="lg"
        className="spd-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-semibold">{previewLesson?.title || previewLesson?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {previewLoading ? (
            <div className="spd-preview-modal__loading"><Spinner animation="border" variant="secondary" /></div>
          ) : previewStreamUrl ? (
            <video
              src={previewStreamUrl}
              controls
              autoPlay
              className="spd-preview-modal__player"
            />
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
}
